import { AxiosInstance, AxiosResponse } from 'axios';
import { GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid';
import { extractApiError } from './apiClient';

/** Server limit on the number of IDs accepted in a single bulk-delete request. */
export const BULK_DELETE_MAX_IDS = 500;

/** A single ID the server refused to delete, with the verbatim reason. */
export interface BulkDeleteFailedItem {
  id: string;
  reason: string;
}

/**
 * Best-effort bulk-delete result. The HTTP envelope is always 200 and `status`
 * is always "success" — branch on `deleted_count` / `failed_count`, never on
 * `status` or the HTTP code.
 */
export interface BulkDeleteResult {
  status: string;
  tenant_id: string;
  requested: number;
  deleted_count: number;
  failed_count: number;
  deleted: string[];
  failed: BulkDeleteFailedItem[];
}

/**
 * Route-specific bulk-delete options sent alongside `ids` in the request body.
 * `force` overrides in-progress / already-sent guards on the routes that support
 * it; cascade flags (e.g. `cascade_dialog_episode`, `cascade_submissions`) are
 * passed through verbatim per route.
 */
export interface BulkDeleteOptions {
  force?: boolean;
  [cascadeKey: string]: boolean | undefined;
}

/** Split an array into chunks of at most `size` items. */
function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * POST a bulk-delete request and return the per-ID result breakdown.
 *
 * Rejects client-side on an empty `ids` list (matching the server's 422
 * contract) before any request is sent. Selections larger than the server
 * limit are split into ≤500-ID chunks, POSTed sequentially, and their result
 * buckets merged into a single aggregate result.
 *
 * Transport failures (401 / 500 / network) reject with an `Error` — they are
 * NOT a `BulkDeleteResult` and must be handled separately by the caller.
 *
 * @param apiClient  A `createApiClient(...)` instance (tagged + authenticated).
 * @param serviceName  Console-log tag, e.g. 'ORDER'.
 * @param path  Bulk-delete route, e.g. `/business-documents/orders/state/acme/bulk-delete`.
 * @param ids  IDs to delete (non-empty).
 * @param options  Route-specific extras merged into the request body.
 */
export async function performBulkDelete(
  apiClient: AxiosInstance,
  serviceName: string,
  path: string,
  ids: string[],
  options: BulkDeleteOptions = {},
): Promise<BulkDeleteResult> {
  if (!ids || ids.length === 0) {
    throw new Error('No items selected for deletion');
  }

  try {
    console.log(`🗑️ [${serviceName}] Bulk deleting:`, {
      path,
      count: ids.length,
      options,
      chunked: ids.length > BULK_DELETE_MAX_IDS,
    });

    const batches = chunk(ids, BULK_DELETE_MAX_IDS);

    const aggregate: BulkDeleteResult = {
      status: 'success',
      tenant_id: '',
      requested: 0,
      deleted_count: 0,
      failed_count: 0,
      deleted: [],
      failed: [],
    };

    for (const batch of batches) {
      const response: AxiosResponse<BulkDeleteResult> = await apiClient.post(path, {
        ids: batch,
        ...options,
      });
      const data = response.data;
      aggregate.tenant_id = data.tenant_id || aggregate.tenant_id;
      aggregate.requested += data.requested ?? 0;
      aggregate.deleted_count += data.deleted_count ?? 0;
      aggregate.failed_count += data.failed_count ?? 0;
      aggregate.deleted.push(...(data.deleted ?? []));
      aggregate.failed.push(...(data.failed ?? []));
    }

    console.log(`✅ [${serviceName}] Bulk delete result:`, {
      requested: aggregate.requested,
      deleted_count: aggregate.deleted_count,
      failed_count: aggregate.failed_count,
    });

    return aggregate;
  } catch (error: any) {
    console.error(`❌ [${serviceName}] Bulk delete error:`, {
      path,
      count: ids.length,
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });
    const wrapped = extractApiError(error, 'Bulk delete failed');
    (wrapped as any).status = error.response?.status;
    throw wrapped;
  }
}

/**
 * Resolve MUI X DataGrid v8's selection model to a concrete list of row IDs.
 *
 * v8's `GridRowSelectionModel` is `{ type: 'include' | 'exclude', ids: Set }`.
 * Header "select all" produces `type: 'exclude'` (all rows except the excluded
 * set), so this expands against the current `rows` rather than the visible page.
 */
export function resolveSelectedIds<T extends GridValidRowModel>(
  model: GridRowSelectionModel,
  rows: T[],
  getRowId: (row: T) => string,
): string[] {
  const ids = model.ids as Set<unknown>;
  if (model.type === 'exclude') {
    return rows
      .map((row) => getRowId(row))
      .filter((id) => !ids.has(id));
  }
  // include mode: only the explicitly selected ids that still exist in rows
  const present = new Set(rows.map((row) => getRowId(row)));
  return Array.from(ids, (id) => String(id)).filter((id) => present.has(id));
}
